import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useNavigate } from "react-router";
import type { UserOnboardingWorkState } from "users/information/user-onboarding-work";

type OnboardingProposalStatePageProps = {
  state: Exclude<UserOnboardingWorkState, "ProposalInProgress" | "AgreementRequired" | "Completed">;
};

const content: Record<OnboardingProposalStatePageProps["state"], { title: string; message: string }> = {
  PreparingProposal: {
    title: "Preparing your proposal",
    message: "AttorneyHub is preparing your proposal. This page will update when it is ready.",
  },
  Finalizing: {
    title: "Finalizing your AttorneyHub agreement",
    message: "Your hire was received. We are completing your account setup now.",
  },
  StaffResolutionRequired: {
    title: "AttorneyHub staff review is required",
    message: "A change needs AttorneyHub staff review. Your account stays restricted while staff resolve it.",
  },
  Closed: {
    title: "Onboarding is closed",
    message: "This onboarding request is closed. Contact support if you need help continuing.",
  },
};

export default function OnboardingProposalStatePage({ state }: Readonly<OnboardingProposalStatePageProps>) {
  const navigate = useNavigate();
  const stateContent = content[state];
  const isStaffReview = state === "StaffResolutionRequired";

  return (
    <Box sx={{ height: "100%", overflow: "auto", p: { xs: 2, sm: 4 } }}>
      <Paper elevation={1} sx={{ maxWidth: 760, mx: "auto", p: { xs: 3, sm: 5 } }}>
        <Stack spacing={2} sx={{ alignItems: "center", textAlign: "center" }}>
          {isStaffReview ? <SupportAgentIcon color="info" sx={{ fontSize: 48 }} /> : <HourglassEmptyIcon color="primary" sx={{ fontSize: 48 }} />}
          <Typography component="h1" variant="h4">{stateContent.title}</Typography>
          <Typography color="text.secondary" variant="body1">{stateContent.message}</Typography>
          {state === "Finalizing" && <Alert severity="info" sx={{ width: "100%" }}>We will open your dashboard when setup is complete.</Alert>}
          {isStaffReview && <Alert severity="warning" sx={{ width: "100%" }}>You can return to your proposal list while staff review the request.</Alert>}
          {state === "Closed" && <Button variant="contained" onClick={() => navigate("/help")}>Contact support</Button>}
        </Stack>
      </Paper>
    </Box>
  );
}
